import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Input } from '@/components/ui/input'
import { api } from '@/lib/api'

export type Source = 'field' | 'context' | 'user' | 'group' | 'project' | 'issuetype' | 'status' | 'priority' | 'resolution' | 'role' | 'service_desk' | 'organization' | 'board' | 'request_type' | 'sprint' | 'workflow_scheme' | 'notification_scheme' | 'permission_scheme' | 'security_scheme' | 'priority_scheme' | 'issue_type_scheme' | 'issue_type_screen_scheme' | 'screen_scheme' | 'screen' | 'field_config_scheme' | 'filter' | 'dashboard' | 'workflow'

type Item = Record<string, unknown>

// Dependent sources resolve their parent value from `deps[<parentKey>]`.
const DEPENDENT_PARENT: Partial<Record<Source, string>> = {
  context: 'field_id',
  request_type: 'service_desk_id',
  sprint: 'board_id',
}

// Build the cache endpoint for a source given its dependency values.
function endpoint(source: Source, deps: Record<string, string>, q: string): string | null {
  const qs = q ? `?q=${encodeURIComponent(q)}` : ''
  if (source === 'field') return `/cache/fields${qs}`
  if (source === 'user') return `/cache/users${qs}`
  if (source === 'group') return `/cache/groups${qs}`
  if (source === 'context') return deps.field_id ? `/cache/fields/${encodeURIComponent(deps.field_id)}/contexts${qs}` : null
  if (source === 'request_type' || source === 'sprint') {
    const parent = deps[DEPENDENT_PARENT[source]!]
    return parent ? `/cache/config/${source}/${encodeURIComponent(parent)}${qs}` : null
  }
  if (['project', 'issuetype', 'status', 'priority', 'resolution', 'role', 'service_desk', 'organization', 'board', 'workflow_scheme', 'notification_scheme', 'permission_scheme', 'security_scheme', 'priority_scheme', 'issue_type_scheme', 'issue_type_screen_scheme', 'screen_scheme', 'screen', 'field_config_scheme', 'filter', 'dashboard', 'workflow'].includes(source)) {
    return `/cache/config/${source}${qs}`
  }
  return null
}

function labelOf(source: Source, it: Item, valueKey?: string): { primary: string; dim: string; value: string } {
  // valueKey lets a param emit a non-default field (e.g. user→email, group→name) per its source hint.
  const emit = (def: string) => (valueKey && it[valueKey] != null ? String(it[valueKey]) : def)
  if (source === 'field') return { primary: String(it.name), dim: `${it.id} · ${it.kind ?? it.field_type ?? ''}`, value: emit(String(it.id)) }
  if (source === 'context') return { primary: String(it.name), dim: String(it.context_id), value: emit(String(it.context_id)) }
  if (source === 'user') return { primary: String(it.display_name), dim: String(it.email ?? ''), value: emit(String(it.account_id)) }
  if (['project', 'issuetype', 'status', 'priority', 'resolution', 'role', 'service_desk', 'organization', 'board', 'request_type', 'sprint', 'workflow_scheme', 'notification_scheme', 'permission_scheme', 'security_scheme', 'priority_scheme', 'issue_type_scheme', 'issue_type_screen_scheme', 'screen_scheme', 'screen', 'field_config_scheme', 'filter', 'dashboard', 'workflow'].includes(source)) {
    return { primary: String(it.name), dim: String(it.key ?? it.id ?? ''), value: emit(String(it.id)) }
  }
  return { primary: String(it.name), dim: String(it.group_id), value: emit(String(it.group_id)) }
}

// value → display name, per source, from every cache response seen this session — lets a
// multi-select chip show "jira-users" instead of a bare groupId once the name is known.
const KNOWN_LABELS = new Map<string, string>()
const labelKey = (source: Source, value: string) => `${source}\u0000${value}`

export function Combobox({
  source,
  valueKey,
  deps,
  multi,
  value,
  onChange,
  onSelect,
  ariaLabel,
  disabled,
  className = 'mt-1',
}: {
  source: Source
  valueKey?: string
  deps: Record<string, string>
  multi: boolean
  value: string
  onChange: (v: string) => void
  onSelect?: (value: string) => void
  ariaLabel?: string
  disabled?: boolean
  /** wrapper classes (default keeps the form spacing) */
  className?: string
}) {
  const [q, setQ] = useState('')
  const [items, setItems] = useState<Item[]>([])
  const [open, setOpen] = useState(false)
  const [empty, setEmpty] = useState(false)
  // the cache request failed — "no match" is then unknown, not confirmed
  const [failed, setFailed] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const reqId = useRef(0)
  const listId = useId()
  const optionId = (i: number) => `${listId}-opt-${i}`
  const containerRef = useRef<HTMLDivElement>(null)
  const fieldRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  // Dropdown is portaled to <body> so it escapes an overflow:hidden clip.
  // We position it (fixed) against the input's viewport rect and keep it pinned on scroll/resize.
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null)

  const url = useMemo(() => endpoint(source, deps, q), [source, deps, q])
  const parentKey = DEPENDENT_PARENT[source]
  const blockedBy = parentKey && !deps[parentKey] ? parentKey : null

  const reposition = useCallback(() => {
    const el = fieldRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setRect({ top: r.bottom + 4, left: r.left, width: r.width })
  }, [])

  useEffect(() => {
    if (!open || !url) return
    const id = ++reqId.current
    // Drop the previous query's options so Enter can't pick a stale match mid-fetch.
    setItems([])
    setEmpty(false)
    setFailed(false)
    const t = setTimeout(() => {
      api<Item[]>(url)
        .then((r) => {
          if (id !== reqId.current) return
          for (const it of r) {
            const l = labelOf(source, it, valueKey)
            KNOWN_LABELS.set(labelKey(source, l.value), l.primary)
          }
          setItems(r)
          setEmpty(r.length === 0)
        })
        .catch(() => { if (id === reqId.current) { setItems([]); setEmpty(true); setFailed(true) } })
    }, 300)
    return () => clearTimeout(t)
  }, [open, url, source, valueKey])

  // Reset the keyboard highlight when the option set or query changes.
  useEffect(() => {
    setHighlightedIndex(0)
  }, [items, q])

  // Keep the portaled dropdown pinned to the input while open.
  useEffect(() => {
    if (!open) return
    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, reposition])

  // Close on outside click — count both the field and the portaled dropdown as "inside".
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      const t = e.target as Node
      const insideField = containerRef.current?.contains(t)
      const insideDropdown = dropdownRef.current?.contains(t)
      if (!insideField && !insideDropdown) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // multi: value is a newline-joined list (matches the array coerce in OperationPage)
  const selected = multi ? value.split('\n').map((s) => s.trim()).filter(Boolean) : []
  const nameOf = (v: string) => KNOWN_LABELS.get(labelKey(source, v))
  const remove = (v: string) => onChange(selected.filter((x) => x !== v).join('\n'))
  const pick = (v: string) => {
    if (multi) {
      const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]
      onChange(next.join('\n'))
      // A pick ends the search: clear the query and close the list (chips show the result).
      setQ('')
      setOpen(false)
      onSelect?.(v)
    } else {
      onChange(v)
      setOpen(false)
      setQ('')
      onSelect?.(v)
    }
  }

  // Keyboard navigation on the search input.
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      if (items.length === 0) return
      setHighlightedIndex((i) => Math.min(i + 1, items.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      if (items.length === 0) return
      setHighlightedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      if (open && items.length > 0 && highlightedIndex >= 0 && highlightedIndex < items.length) {
        e.preventDefault()
        const it = items[highlightedIndex]
        pick(labelOf(source, it, valueKey).value)
      } else if (multi && q.trim() && empty && !failed) {
        // The cache confirmed no match (not merely still loading): Enter adds the raw id.
        e.preventDefault()
        const raw = q.trim()
        if (!selected.includes(raw)) pick(raw)
        else { setQ(''); setOpen(false) }
      }
    } else if (e.key === 'Backspace') {
      if (multi && q === '' && selected.length > 0) {
        e.preventDefault()
        remove(selected[selected.length - 1])
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault()
        setOpen(false)
      }
    }
    // Tab: leave default behavior so focus moves naturally.
  }

  if (blockedBy) {
    return (
      <Input
        disabled
        placeholder={`Select ${blockedBy} first`}
        className="mt-1 font-mono text-sm"
      />
    )
  }

  const dropdown = (
    <div
      ref={dropdownRef}
      role="listbox"
      id={listId}
      className="max-h-64 overflow-auto rounded-md border border-line-strong bg-popover"
      style={{
        position: 'fixed',
        top: rect?.top ?? 0,
        left: rect?.left ?? 0,
        width: rect?.width ?? 0,
        zIndex: 50,
      }}
    >
      {items.length === 0 ? (
        <div className="px-3 py-2.5 text-[12px] text-text-2">
          {empty
            ? <span>No cache match — type a raw id, or Sync from Jira in Settings → Jira cache</span>
            : <span className="text-text-3">Loading…</span>
          }
        </div>
      ) : (
        <>
          {/* Section header */}
          <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-[0.08em] text-text-3">
            {source}
          </div>
          {items.map((it, i) => {
            const l = labelOf(source, it, valueKey)
            const isSelected = multi ? selected.includes(l.value) : value === l.value
            const isHighlighted = i === highlightedIndex
            // Highlight (keyboard/mouse) wins over the selected tint for bg,
            // so the active descendant is always visible.
            const background = isHighlighted
              ? 'var(--surface-selected)'
              : isSelected
                ? 'var(--surface-raised)'
                : undefined
            return (
              <button
                key={l.value}
                type="button"
                role="option"
                id={optionId(i)}
                aria-selected={isHighlighted}
                onClick={() => pick(l.value)}
                className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left transition-colors"
                style={{ background }}
                // Mouse hover keeps the keyboard highlight in sync.
                onMouseEnter={() => setHighlightedIndex(i)}
              >
                <span className="flex min-w-0 items-center gap-1.5 truncate text-[13px] text-foreground">
                  {multi && (
                    <span className={isSelected ? 'text-primary' : 'text-transparent'} aria-hidden>✓</span>
                  )}
                  {l.primary}
                </span>
                <span className="shrink-0 font-mono text-[11px] text-text-3">
                  {l.dim}
                </span>
              </button>
            )
          })}
        </>
      )}
    </div>
  )

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Multi-select chips — above the input so the current selection is the first thing seen */}
      {multi && selected.length > 0 && (
        <ul aria-label={`Selected ${ariaLabel ?? source}`} className="mb-1.5 flex flex-wrap gap-1.5">
          {selected.map((v) => {
            const name = nameOf(v)
            return (
              <li
                key={v}
                className="flex items-center gap-1.5 rounded-md border border-line-strong bg-surface-raised py-0.5 pl-2 pr-0.5 text-[12px]"
              >
                <span className="text-foreground">{name ?? v}</span>
                {name && <span className="font-mono text-[11px] text-text-3">{v}</span>}
                <button
                  type="button"
                  onClick={() => remove(v)}
                  aria-label={`Remove ${name ?? v}`}
                  className="flex h-5 w-5 items-center justify-center rounded text-text-3 transition-colors hover:bg-surface-selected hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                >
                  <span aria-hidden>×</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {/* Search / raw-id input */}
      <div ref={fieldRef} className="relative">
        <Input
          value={multi ? q : value || q}
          placeholder={multi ? (selected.length > 0 ? 'Add another…' : 'Search or type a raw id') : 'Search or type a raw id'}
          role="combobox"
          aria-label={ariaLabel}
          disabled={disabled}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && items.length > 0 && highlightedIndex >= 0
              ? optionId(highlightedIndex)
              : undefined
          }
          onFocus={() => setOpen(true)}
          // after a multi pick the list closes with focus kept — a click reopens it
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onChange={(e) => {
            setQ(e.target.value)
            if (!multi) onChange(e.target.value)
            setOpen(true)
          }}
          className="h-8 pr-12 font-mono text-[12.5px] md:text-[12.5px] placeholder:font-sans placeholder:text-text-3"
        />
        {/* Source badge — tiny mono label right-inside the input */}
        <span
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-text-3"
          aria-hidden
        >
          {source}
        </span>
      </div>

      {/* Dropdown — portaled to body so overflow:hidden cannot clip it */}
      {open && rect && createPortal(dropdown, document.body)}
    </div>
  )
}
